import Image from "next/image";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import copyClipboard from "clipboard-copy";
import { BsClipboardCheck, BsClipboard } from "react-icons/bs";
import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";

import {
  GiftPurchaseResponse,
  RecipientDetailsType,
} from "./usePurchaseGiftCard";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";

import { useTeamConfig } from "@/global/hooks/useTeamConfig";

import GiftImage from "@/assets/images/Gift.svg";
import { useTranslation } from "react-i18next";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  recipientForm: ReturnType<typeof useForm>;
  purchaseResponse: GiftPurchaseResponse | null;
  deliveryDate?: string | null;
}

export function PurchaseGiftSuccessModal(props: Props) {
  const { languages } = useTeamConfig();
  const recipientDetails =
    props.recipientForm.getValues() as RecipientDetailsType;
  const purchaseResponse = props.purchaseResponse;
  const { t } = useTranslation();
  const isScheduledDelivery = Boolean(props.deliveryDate);
  const modalTitle =
    t("giftCard.checkout.successModal.title") ||
    languages?.purchaseGiftCard?.purchaseGiftCardSuccessLanguage ||
    "Success!";

  return (
    <NotificationModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      onClickPrimaryButton={props.onClose}
      title={modalTitle}
      hideSecondaryButton
      primaryButtonText={
        t("giftCard.checkout.successModal.closeButtonText") || "Close"
      }
      hideTitleIcon
      description={
        <Box>
          <Stack alignItems={"center"} spacing={"16px"} mb={"24px"}>
            <Box>
              <Image
                src={GiftImage}
                width={133}
                height={133}
                alt={"gift dollar icon"}
              />
            </Box>
            <Stack>
              <Typography
                fontSize={"26px"}
                fontWeight={700}
                textAlign={"center"}
                variant="body1"
              >
                {recipientDetails.email ? (
                  <>
                    {isScheduledDelivery
                      ? t(
                          "giftCard.checkout.successModal.scheduledSentToRecipientText"
                        ) || "Your Gift Card Will Be Sent To"
                      : t(
                          "giftCard.checkout.successModal.sentToRecipientText"
                        ) || "Your Gift Card Was Sent To"}
                  </>
                ) : (
                  <>
                    {t("giftCard.checkout.successModal.purchaseSuccessText") ||
                      "Gift Card Purchase Successful"}
                  </>
                )}
              </Typography>
              {recipientDetails.email && (
                <Typography
                  fontSize={"26px"}
                  fontWeight={400}
                  color="text.secondary"
                  textAlign={"center"}
                  variant="body1"
                  lineHeight={1}
                >
                  {recipientDetails.email}
                </Typography>
              )}

              <Typography
                mt={1}
                fontSize={"16px"}
                fontWeight={400}
                color="text.primary"
                textAlign={"center"}
                variant="body1"
                lineHeight={1}
              >
                {isScheduledDelivery
                  ? t(
                      "giftCard.checkout.successModal.scheduledInstructionsText"
                    ) ||
                    "Your gift card will be delivered on your scheduled date. The recipient will receive activation instructions via email."
                  : t("giftCard.checkout.successModal.instructionsText") ||
                    "Instructions on how to redeem the gift card were included in the email."}
              </Typography>
            </Stack>
          </Stack>
          <Stack
            alignItems={"center"}
            spacing={"16px"}
            justifyContent={"space-between"}
          >
            <Stack alignItems={"center"} spacing={"8px"}>
              {purchaseResponse?.couponCode && (
                <>
                  {/* <Typography fontSize={"24px"} textAlign={"center"}>
                    Your code is:{" "}
                    <span style={{ fontWeight: 700 }}>
                      {purchaseResponse.couponCode}
                    </span>{" "}
                    {purchaseResponse.amount && (
                      <>
                        {" "}
                        with total value of{" "}
                        <span style={{ fontWeight: 700 }}>
                          {getUSCurrency(Number(purchaseResponse.amount))}
                        </span>{" "}
                      </>
                    )}
                  </Typography> */}
                  {purchaseResponse.orderNumber && (
                    <Typography fontSize={"16px"} textAlign={"center"}>
                      {t("giftCard.checkout.successModal.orderNumberLabel") ||
                        "Your order number is:"}{" "}
                      <span style={{ fontWeight: 700 }}>
                        #{purchaseResponse.orderNumber}
                      </span>
                    </Typography>
                  )}
                </>
              )}
            </Stack>
            {/* <Stack alignItems={"center"} spacing={"8px"}>
              {purchaseResponse?.couponCode ? (
                <ClipboardButton clipboardText={purchaseResponse?.couponCode} />
              ) : null}

              {!recipientDetails.email && (
                <Typography fontSize={"16px"} textAlign={"center"}>
                  Please copy the gift card code and send it to the recipient to
                  be redeemed{" "}
                  <Link
                    href={{
                      pathname: routes.redeem,
                      query: { teamSchema },
                    }}
                  >
                    HERE
                  </Link>
                </Typography>
              )}
            </Stack> */}
          </Stack>
        </Box>
      }
    />
  );
}

const ClipboardButton = ({ clipboardText }: { clipboardText: string }) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isCopiedTooltip, seIsCopiedTooltip] = useState(false);
  const btnRef = useRef(null);

  const handleCopy = () => {
    copyClipboard(clipboardText).then(() => {
      setIsCopied(true);
      seIsCopiedTooltip(true);
      setTimeout(() => {
        seIsCopiedTooltip(false);
      }, 1500);
    });
  };

  return (
    <Tooltip title="Copied!" open={isCopiedTooltip} placement="top">
      <Button
        ref={btnRef}
        size="large"
        variant={isCopied ? "outlined" : "contained"}
        endIcon={isCopied ? <BsClipboardCheck /> : <BsClipboard />}
        sx={{
          fontSize: "24px",
        }}
        onClick={handleCopy}
      >
        {isCopied ? "Copied!" : "Copy Code"}
      </Button>
    </Tooltip>
  );
};
